// alert — wireframe skin module
// Token disposition: color tokens overridden to wf-* namespace; spacing/sizing pass-through

const _ICON_INFO = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
  <circle cx="8" cy="8" r="7" stroke="currentColor" stroke-width="1.5"/>
  <path d="M8 7v5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
  <circle cx="8" cy="4.5" r="0.75" fill="currentColor"/>
</svg>`;

const _ICON_SUCCESS = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
  <circle cx="8" cy="8" r="7" stroke="currentColor" stroke-width="1.5"/>
  <path d="M5 8l2.5 2.5L11 5.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`;

const _ICON_WARNING = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
  <path d="M8 2L14.5 13.5H1.5L8 2Z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>
  <path d="M8 6.5v3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
  <circle cx="8" cy="11" r="0.75" fill="currentColor"/>
</svg>`;

const _ICON_ERROR = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
  <circle cx="8" cy="8" r="7" stroke="currentColor" stroke-width="1.5"/>
  <path d="M5.5 5.5l5 5M10.5 5.5l-5 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
</svg>`;

const _ICON_CLOSE = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
  <path d="M2 2l10 10M12 2L2 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
</svg>`;

export const styles = `
  :host {
    display: block;
    box-sizing: border-box;
    font-family: var(--run-wf-font-family);
  }

  :host(:not([open])) {
    display: none;
  }

  :host([closing]) {
    opacity: 0;
  }

  [part="base"] {
    display: flex;
    align-items: flex-start;
    gap: var(--run-alert-gap, var(--run-space-semantic-xs));
    padding: var(--run-alert-padding, var(--run-space-semantic-xs) var(--run-space-semantic-sm));
    border-radius: var(--run-alert-border-radius, var(--run-radius-semantic-control));
    box-sizing: border-box;
    width: 100%;
    background: var(--run-wf-surface);
    color: var(--run-wf-text);
  }

  /* ── subtle appearance (default) ── */
  :host([appearance="subtle"]) [part="base"],
  :host(:not([appearance])) [part="base"] {
    border-left: 2px dashed var(--run-wf-border);
  }

  /* variant × subtle — all variants use same wf palette; border color unified */
  :host([variant="info"]) [part="base"],
  :host(:not([variant])) [part="base"] {
    background: var(--run-wf-surface);
    border-color: var(--run-wf-border);
    color: var(--run-wf-text);
  }
  :host([variant="success"]) [part="base"] {
    background: var(--run-wf-surface);
    border-color: var(--run-wf-border);
    color: var(--run-wf-text);
  }
  :host([variant="warning"]) [part="base"] {
    background: var(--run-wf-surface);
    border-color: var(--run-wf-border);
    color: var(--run-wf-text);
  }
  :host([variant="error"]) [part="base"] {
    background: var(--run-wf-surface);
    border-color: var(--run-wf-border);
    color: var(--run-wf-text);
  }
  :host([variant="neutral"]) [part="base"] {
    background: var(--run-wf-surface);
    border-color: var(--run-wf-border);
    color: var(--run-wf-text);
  }

  /* ── solid appearance ── */
  :host([appearance="solid"]) [part="base"] {
    border: 1px dashed var(--run-wf-border);
    background: var(--run-wf-surface);
    color: var(--run-wf-text);
  }
  :host([appearance="solid"][variant="info"]) [part="base"],
  :host([appearance="solid"]:not([variant])) [part="base"] {
    background: var(--run-wf-surface);
    color: var(--run-wf-text);
  }
  :host([appearance="solid"][variant="success"]) [part="base"] {
    background: var(--run-wf-surface);
    color: var(--run-wf-text);
  }
  :host([appearance="solid"][variant="warning"]) [part="base"] {
    background: var(--run-wf-surface);
    color: var(--run-wf-text);
  }
  :host([appearance="solid"][variant="error"]) [part="base"] {
    background: var(--run-wf-surface);
    color: var(--run-wf-text);
  }
  :host([appearance="solid"][variant="neutral"]) [part="base"] {
    background: var(--run-wf-surface);
    color: var(--run-wf-text);
  }

  /* ── icon part ── */
  [part="icon"] {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 16px;
    height: 16px;
  }

  /* ── content ── */
  [part="content"] {
    display: flex;
    flex-direction: column;
    gap: var(--run-alert-gap, var(--run-space-semantic-xs));
    flex-grow: 1;
    min-width: 0;
  }

  [part="title"] {
    font-weight: 400;
    font-family: var(--run-wf-font-family);
    color: var(--run-wf-text);
  }

  [part="title"][hidden] {
    display: none;
  }

  [part="description"] {
    color: var(--run-wf-text);
    font-family: var(--run-wf-font-family);
  }

  [part="action"] {
    display: flex;
    flex-wrap: wrap;
    gap: var(--run-alert-gap, var(--run-space-semantic-xs));
  }

  [part="action"][hidden] {
    display: none;
  }

  /* ── close button ── */
  [part="close-button"] {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 24px;
    height: 24px;
    padding: 0;
    border: 1px dashed var(--run-wf-border);
    background: transparent;
    cursor: pointer;
    color: var(--run-wf-text);
    border-radius: var(--run-alert-border-radius, var(--run-radius-semantic-control));
    margin-left: auto;
    font-family: var(--run-wf-font-family);
  }

  [part="close-button"]:focus-visible {
    outline: 2px dashed var(--run-wf-border);
    outline-offset: 2px;
  }

  [part="close-button"]:hover {
    opacity: 0.75;
  }

  @media (prefers-reduced-motion: reduce) {
    :host([closing]) {
      transition: none;
    }
  }
`;

export function render(host) {
  const variant = host.variant;
  const appearance = host.appearance;
  const dismissible = host.dismissible;
  const open = host.open;
  const title = host.title;
  const hideIcon = host['hide-icon'];
  const role = host.role;
  const _closing = host._closing;

  // Determine role/aria-live attributes for base element
  let roleAttr = '';
  let ariaLiveAttr = '';
  if (role === 'alert') {
    roleAttr = 'role="alert"';
    ariaLiveAttr = 'aria-live="assertive"';
  } else if (role === 'status') {
    roleAttr = 'role="status"';
    ariaLiveAttr = 'aria-live="polite"';
  }
  // role="none" → no role or aria-live attributes

  // Determine variant icon SVG
  let iconSvg = '';
  if (!hideIcon) {
    if (variant === 'info' || !variant) {
      iconSvg = _ICON_INFO;
    } else if (variant === 'success') {
      iconSvg = _ICON_SUCCESS;
    } else if (variant === 'warning') {
      iconSvg = _ICON_WARNING;
    } else if (variant === 'error') {
      iconSvg = _ICON_ERROR;
    }
    // neutral: empty icon part, no SVG children
  }

  const iconPart = hideIcon
    ? ''
    : `<span part="icon" aria-hidden="true">${iconSvg}<slot name="icon"></slot></span>`;

  const titleHidden = (!title || title === '') ? ' hidden' : '';
  const titlePart = `<span part="title"${titleHidden}><slot name="title"></slot></span>`;

  const closeButtonPart = dismissible
    ? `<button part="close-button" aria-label="Dismiss" type="button">${_ICON_CLOSE}</button>`
    : '';

  host.shadowRoot.innerHTML = `
    <div part="base" ${roleAttr} ${ariaLiveAttr} aria-atomic="true">
      ${iconPart}
      <div part="content">
        ${titlePart}
        <div part="description"><slot></slot></div>
        <div part="action"><slot name="action"></slot></div>
      </div>
      ${closeButtonPart}
    </div>
  `;

  // Set title text safely — never via innerHTML
  if (title && title !== '') {
    const titleEl = host.shadowRoot.querySelector('[part="title"]');
    if (titleEl) {
      const textNode = document.createTextNode(title);
      titleEl.insertBefore(textNode, titleEl.firstChild);
    }
  }
}